/**
 * The one place that answers "which languages may this account pick, and what
 * happens when it picks one" (COL-478).
 *
 * Settings → Language and the header switch both run through this. They used
 * to disagree: Settings offered `selectableLanguages(...)` and wrote
 * `profiles.preferred_language`, while the header hard-coded `en ⇄ de` and
 * wrote nothing but localStorage. A French tenant who touched the header
 * landed on German with no way back, and the browser was then pinned there —
 * LanguageProvider only seeds from the account while the browser holds no
 * explicit choice, so the account's French never applied again on that device.
 *
 * Two rules worth keeping in mind when reading `options`:
 *
 *  - A signed-in account may pick any of the six only on the surfaces that
 *    offer them — the tenant surfaces and the portals, per
 *    `offersAllLanguages`. The marketing site offers German and English to
 *    everyone, signed in or not: those pages render de/en only, and a
 *    six-language menu there promised languages the site cannot speak
 *    (COL-477 follow-up, Neel 6 Oct).
 *
 *  - On a full-menu surface the active language is always in the list, even
 *    when the audience rule would not grant it — a signed-out visitor on a
 *    browser where a tenant picked Arabic still sees Arabic offered there.
 *    The control must never be a dead end: whatever language you are in has
 *    to be one you can return to. The marketing site has no dead end to
 *    avoid: it renders de/en whatever is stored, so the control reports the
 *    base language actually showing (`baseLanguageFor`), not the stored
 *    preference, and offers exactly de/en.
 *
 * Three things the save deliberately does NOT do:
 *
 *  - It does not write the account preference from the marketing site — the
 *    header there is the browser-local toggle it was before COL-478. The
 *    control shows the collapsed base language, not the stored choice, so a
 *    write would silently retarget a preference it never displayed — a
 *    French tenant flipping the marketing page to German must not have their
 *    dashboard and emails turned German by it. And it cannot write "only
 *    when the shown language is the real one": after that tenant's first
 *    (skipped) flip the context language IS base, so their next ordinary
 *    de/en click would pass such a check and overwrite the stored French —
 *    see the choose() comment. The switch still applies to this browser;
 *    the stored preference is edited where it is displayed truthfully.
 *
 *  - It does not touch the account while an admin is acting as a tenant.
 *    `impersonate()` swaps the real session, so `user.id` IS the tenant's and
 *    a plain write would set that tenant's stored language, stamp their
 *    `language_chosen_at` (retiring their first-login prompt) and change the
 *    language their emails are sent in — because an admin switched the
 *    interface to read it. TenantLanguagePicker already refuses to decide a
 *    tenant's language this way; so does this. The switch still applies
 *    locally, which is all the admin needs to read the screen.
 *
 *  - It does not let two picks race. The queue is module-level, not per hook
 *    instance: the header and Settings are separate instances of this hook
 *    but there is only one account preference. Without it a quick DE→EN→DE
 *    can land out of order and leave the profile holding a language this
 *    browser is not showing — invisible on this device, wrong on the next
 *    one the user signs in on.
 */
import { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { languagePromptQueryKey } from "@/components/dashboard/LanguagePickerDialog";
import { baseLanguageFor, type SupportedLanguage } from "@/lib/i18n/languages";
import {
  offersAllLanguages,
  selectableLanguages,
} from "@/lib/i18n/tenant-languages";

/**
 * Serialises profile writes across every mounted language control, and drops
 * a write that a later pick has already superseded. Module-level on purpose
 * (see the note above): one account preference, one queue.
 */
let saveQueue: Promise<void> = Promise.resolve();
let lastRequested: SupportedLanguage | null = null;

export type LanguagePreference = {
  /**
   * The language in effect right now, as this SURFACE shows it. Always
   * present in `options`. On the marketing site this is the base language
   * actually rendering, not the stored preference — a tenant who picked
   * Ukrainian reads the marketing pages in English, and the control says EN.
   */
  language: SupportedLanguage;
  /** What this account may switch to, in display order. */
  options: readonly SupportedLanguage[];
  /** Apply + persist. Resolves once the save settled (or immediately if none). */
  choose: (next: SupportedLanguage) => Promise<void>;
  saving: boolean;
  /** False for a signed-out visitor, who keeps German and English. */
  isSignedIn: boolean;
};

/**
 * @param announce Toast on a successful save. Settings does (the card is the
 *   whole point of the screen); the header does not — the page changing
 *   language under you is its own confirmation. Failures always toast: a
 *   preference that silently did not stick is the bug this hook exists for.
 */
export function useLanguagePreference(announce = false): LanguagePreference {
  const { language, setLanguage, t } = useLanguage();
  const { user, isImpersonating } = useAuth();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);

  const isSignedIn = !!user;
  const { pathname } = useLocation();
  const surfaceOffersAll = offersAllLanguages(pathname);
  // What this surface is actually showing: the stored choice on full-menu
  // surfaces, its base collapse on the marketing site, which renders de/en
  // whatever the account holds (LanguageContext keys its dictionary on the
  // same rule via rendersInBaseLanguage).
  const surfaceLanguage = surfaceOffersAll
    ? language
    : baseLanguageFor(language);

  const options = useMemo(() => {
    const selectable = selectableLanguages({
      isSignedIn,
      surfaceOffersAll,
    });
    return selectable.includes(surfaceLanguage)
      ? selectable
      : [...selectable, surfaceLanguage];
  }, [isSignedIn, surfaceOffersAll, surfaceLanguage]);

  const userId = user?.id;

  const choose = useCallback(
    async (next: SupportedLanguage) => {
      // Compare against what this surface is SHOWING: on marketing, picking
      // the base language already on screen is a no-op even when the stored
      // preference differs.
      if (next === surfaceLanguage) return;
      setLanguage(next);
      // Signed out there is no account to save to — the switch is this
      // browser's, which is all a marketing visitor has. Impersonating, there
      // is an account but it is not the clicker's: see the note above.
      if (!userId || isImpersonating) return;
      // The marketing header never edits the stored preference — it is the
      // browser-local toggle it was before COL-478. It cannot be allowed to
      // write conditionally either: the toggle shows the base collapse, not
      // the stored choice, so a French tenant's first flip must not write —
      // and after that flip the context language IS base, so any "write when
      // the control showed the truth" rule turns their second ordinary de/en
      // click into a silent overwrite of the stored French (and a reload
      // forgets the divergence, so no flag can make that rule safe). The
      // account is edited where it is displayed truthfully — the tenant
      // surfaces and the portals' Settings cards.
      if (!surfaceOffersAll) return;

      lastRequested = next;
      setSaving(true);
      const run = saveQueue.then(async () => {
        // A later pick won while this one waited its turn; writing now would
        // overwrite it.
        if (lastRequested !== next) return;
        try {
          const { error } = await supabase
            .from("profiles")
            .update({
              preferred_language: next,
              language_chosen_at: new Date().toISOString(),
            })
            .eq("id", userId);
          if (error) throw error;
          // Closes the tenant dashboard's first-login picker if it is open.
          queryClient.invalidateQueries({
            queryKey: languagePromptQueryKey(userId),
          });
          // Legacy mirror, best effort: notify-property-decision still reads
          // user_metadata.preferred_language until COL-480 moves every email
          // onto the profiles column. Nothing else reads it.
          void supabase.auth.updateUser({ data: { preferred_language: next } });
          if (announce) {
            toast.success(t("settings.language.saved", "Language updated."));
          }
        } catch (error: unknown) {
          toast.error(
            error instanceof Error
              ? error.message
              : t(
                  "settings.language.saveFailed",
                  "Language changed here, but we couldn't save it to your account."
                )
          );
        }
      });
      // Keep the chain alive: one failed save must not wedge every later one.
      saveQueue = run.catch(() => undefined);
      try {
        await run;
      } finally {
        setSaving(false);
      }
    },
    [
      announce,
      isImpersonating,
      queryClient,
      setLanguage,
      surfaceLanguage,
      surfaceOffersAll,
      t,
      userId,
    ]
  );

  return {
    language: surfaceLanguage,
    options,
    choose,
    saving,
    isSignedIn,
  };
}

export default useLanguagePreference;
